import React, { useState, useEffect, useCallback } from 'react';
import {
  Plus, Trash2, X, Loader2, CheckCircle, AlertCircle, Play,
  ChevronDown, ChevronRight, GitBranch, ArrowDown, History,
  Zap, Save, Activity, Clock,
} from 'lucide-react';
import {
  AgentPipeline, AgentPipelineCreate, AgentPipelineUpdate,
  PipelineNode, PipelineEdge, ActionNode, EdgeCondition, PipelineExecution, NodeResult,
} from '../types/pipeline';
import * as pipelineSvc from '../services/pipelineService';
import { TradingAgent } from '../types/agent';
import { getAgents } from '../services/agentService';
import { DataConnection } from '../types/connection';
import { getConnections } from '../services/connectionService';
import ConfirmationModal from '../components/ConfirmationModal';
import { formatDate } from '../utils';

// ---------------------------------------------------------------------------
// Local state shapes
// ---------------------------------------------------------------------------

interface StepRow {
  nodeId: string;
  agent_id: string;
  label: string;
  // Incoming edge from the previous step (undefined for first step)
  inCondition: EdgeCondition | null;
  dataMapping: Record<string, string>;
  showMapping: boolean;
}

interface ActionForm {
  id: string;
  node_id: string;
  action_type: 'execute_trade' | 'notify_slack' | 'push_mcp' | 'notify_telegram' | 'notify_mattermost' | 'trigger_signal_window';
  // execute_trade
  mode: 'live' | 'paper';
  connection_id: string;
  symbol: string;
  qty_field: string;
  // notify_slack
  slack_template: string;
  // push_mcp
  mcp_connection_id: string;
  // notify_telegram
  telegram_connection_id: string;
  telegram_chat: string;
  telegram_template: string;
  // notify_mattermost
  mattermost_connection_id: string;
  mattermost_channel_id: string;
  mattermost_template: string;
  // trigger_signal_window
  signal_agent_id: string;
  trigger_window_duration: number;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const EDGE_OPS = ['==', '!=', '>', '<', '>=', '<=', 'in', 'not_in', 'contains'];

function genId(): string {
  return Math.random().toString(36).slice(2, 10);
}

function stepsToApi(
  steps: StepRow[],
  actions: ActionForm[],
): { nodes: PipelineNode[]; edges: PipelineEdge[]; entry_node: string | null; action_nodes: ActionNode[] } {
  const nodes: PipelineNode[] = steps.map((s, i) => ({
    id: s.nodeId,
    agent_id: s.agent_id,
    label: s.label || `Step ${i + 1}`,
  }));

  const edges: PipelineEdge[] = [];
  for (let i = 1; i < steps.length; i++) {
    const step = steps[i];
    edges.push({
      id: `e-${steps[i - 1].nodeId}-${step.nodeId}`,
      source: steps[i - 1].nodeId,
      target: step.nodeId,
      condition: step.inCondition,
      data_mapping: step.dataMapping,
    });
  }

  const action_nodes: ActionNode[] = actions.map((a) => {
    let config: Record<string, unknown> = {};
    if (a.action_type === 'execute_trade') {
      config = {
        mode: a.mode,
        connection_id: a.connection_id || null,
        symbol: a.symbol,
        qty_field: a.qty_field || 'position_size_pct',
      };
    } else if (a.action_type === 'notify_slack') {
      config = { template: a.slack_template };
    } else if (a.action_type === 'push_mcp') {
      config = { connection_id: a.mcp_connection_id || null };
    } else if (a.action_type === 'notify_telegram') {
      config = {
        connection_id: a.telegram_connection_id || null,
        chat: a.telegram_chat || null,
        message_template: a.telegram_template || null,
      };
    } else if (a.action_type === 'notify_mattermost') {
      config = {
        connection_id: a.mattermost_connection_id || null,
        channel_id: a.mattermost_channel_id || null,
        message_template: a.mattermost_template || null,
      };
    } else if (a.action_type === 'trigger_signal_window') {
      config = {
        agent_id: a.signal_agent_id || null,
        window_duration_minutes: a.trigger_window_duration || 60,
      };
    }
    return { node_id: a.node_id, action_type: a.action_type, config };
  });

  return { nodes, edges, entry_node: steps[0]?.nodeId || null, action_nodes };
}

function apiToSteps(pipeline: AgentPipeline): { steps: StepRow[]; actions: ActionForm[] } {
  // Reconstruct ordered step list following entry_node → edges
  const nodeMap = new Map(pipeline.nodes.map((n) => [n.id, n]));
  const edgeBySource = new Map(pipeline.edges.map((e) => [e.source, e]));
  const ordered: PipelineNode[] = [];
  let current: string | null = pipeline.entry_node;
  const visited = new Set<string>();

  while (current && nodeMap.has(current) && !visited.has(current)) {
    visited.add(current);
    ordered.push(nodeMap.get(current)!);
    const edge = edgeBySource.get(current);
    current = edge ? edge.target : null;
  }
  // Append any orphan nodes
  for (const n of pipeline.nodes) {
    if (!visited.has(n.id)) ordered.push(n);
  }

  const steps: StepRow[] = ordered.map((n, i) => {
    const inEdge = i > 0 ? pipeline.edges.find((e) => e.target === n.id) : null;
    return {
      nodeId: n.id,
      agent_id: n.agent_id,
      label: n.label,
      inCondition: inEdge?.condition || null,
      dataMapping: inEdge?.data_mapping || {},
      showMapping: inEdge ? Object.keys(inEdge.data_mapping || {}).length > 0 : false,
    };
  });

  const actions: ActionForm[] = (pipeline.action_nodes || []).map((a) => ({
    id: genId(),
    node_id: a.node_id,
    action_type: a.action_type,
    mode: (a.config.mode as 'live' | 'paper') || 'paper',
    connection_id: (a.config.connection_id as string) || '',
    symbol: (a.config.symbol as string) || '',
    qty_field: (a.config.qty_field as string) || 'position_size_pct',
    slack_template: (a.config.template as string) || '',
    mcp_connection_id: (a.config.connection_id as string) || '',
    telegram_connection_id: (a.config.connection_id as string) || '',
    telegram_chat: (a.config.chat as string) || '',
    telegram_template: (a.config.message_template as string) || '',
    mattermost_connection_id: (a.config.connection_id as string) || '',
    mattermost_channel_id: (a.config.channel_id as string) || '',
    mattermost_template: (a.config.message_template as string) || '',
    signal_agent_id: (a.config.agent_id as string) || '',
    trigger_window_duration: (a.config.window_duration_minutes as number) || 60,
  }));

  return { steps, actions };
}

function durationLabel(started: string, completed: string | null): string {
  if (!completed) return '—';
  const ms = new Date(completed).getTime() - new Date(started).getTime();
  return ms < 1000 ? `${ms}ms` : `${(ms / 1000).toFixed(1)}s`;
}

// ---------------------------------------------------------------------------
// NodeStatusBadge
// ---------------------------------------------------------------------------

const NodeStatusBadge: React.FC<{ result?: NodeResult; running?: boolean }> = ({ result, running }) => {
  if (running) return (
    <span className="flex items-center gap-1 text-xs text-blue-300">
      <Loader2 className="w-3 h-3 animate-spin" /> Running
    </span>
  );
  if (!result) return null;
  if (result.skipped) return (
    <span className="text-xs text-slate-500">Skipped</span>
  );
  if (result.success) return (
    <span className="flex items-center gap-1 text-xs text-green-400">
      <CheckCircle className="w-3 h-3" />
      {result.duration_ms ? `${result.duration_ms.toFixed(0)}ms` : 'Done'}
    </span>
  );
  return (
    <span className="flex items-center gap-1 text-xs text-red-400">
      <AlertCircle className="w-3 h-3" /> Failed
    </span>
  );
};

// ---------------------------------------------------------------------------
// ConditionEditor — between steps
// ---------------------------------------------------------------------------

interface ConditionEditorProps {
  condition: EdgeCondition | null;
  onChange: (c: EdgeCondition | null) => void;
}

const ConditionEditor: React.FC<ConditionEditorProps> = ({ condition, onChange }) => {
  const [open, setOpen] = useState(!!condition);

  const toggle = () => {
    if (open) {
      setOpen(false);
      onChange(null);
    } else {
      setOpen(true);
      onChange({ field: 'signal', op: '==', value: 'buy' });
    }
  };

  const set = (field: keyof EdgeCondition, value: unknown) => {
    onChange({ ...(condition || { field: 'signal', op: '==', value: 'buy' }), [field]: value } as EdgeCondition);
  };

  return (
    <div className="flex flex-col items-center gap-1 my-1">
      <ArrowDown className="w-4 h-4 text-slate-600" />
      <button
        onClick={toggle}
        className={`text-xs px-2.5 py-1 rounded-full border transition ${
          open
            ? 'border-blue-500/60 bg-blue-500/10 text-blue-300'
            : 'border-slate-600 bg-slate-800 text-slate-500 hover:text-slate-300'
        }`}
      >
        {open ? 'Conditional edge' : '+ Add condition'}
      </button>
      {open && condition && (
        <div className="flex items-center gap-2 mt-1 bg-slate-800/70 border border-blue-500/20 rounded-lg px-3 py-2">
          <input
            value={condition.field}
            onChange={(e) => set('field', e.target.value)}
            placeholder="field (e.g. signal)"
            className="bg-transparent border-b border-slate-600 text-blue-300 text-xs w-28 focus:outline-none focus:border-blue-400 pb-0.5"
          />
          <select
            value={condition.op}
            onChange={(e) => set('op', e.target.value)}
            className="bg-slate-900 border border-slate-600 rounded px-1.5 py-0.5 text-xs text-slate-300 focus:outline-none"
          >
            {EDGE_OPS.map((op) => <option key={op} value={op}>{op}</option>)}
          </select>
          <input
            value={condition.value == null ? '' : String(condition.value as string | number | boolean)}
            onChange={(e) => set('value', e.target.value)}
            placeholder="value"
            className="bg-transparent border-b border-slate-600 text-blue-300 text-xs w-20 focus:outline-none focus:border-blue-400 pb-0.5"
          />
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// DataMappingEditor
// ---------------------------------------------------------------------------

interface DataMappingProps {
  mapping: Record<string, string>;
  show: boolean;
  onToggle: () => void;
  onChange: (m: Record<string, string>) => void;
}

const DataMappingEditor: React.FC<DataMappingProps> = ({ mapping, show, onToggle, onChange }) => {
  const entries = Object.entries(mapping);

  const addPair = () => onChange({ ...mapping, '': '' });
  const updatePair = (oldKey: string, newKey: string, val: string) => {
    const next: Record<string, string> = {};
    for (const [k, v] of Object.entries(mapping) as [string, string][]) {
      if (k === oldKey) next[newKey] = val;
      else next[k] = v;
    }
    onChange(next);
  };
  const removePair = (key: string) => {
    const next = { ...mapping };
    delete next[key];
    onChange(next);
  };

  return (
    <div className="pl-4 border-l border-slate-700 ml-4">
      <button
        onClick={onToggle}
        className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-300 transition py-1"
      >
        {show ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
        Data mapping from previous step
        {entries.length > 0 && (
          <span className="px-1.5 py-0.5 rounded-full bg-slate-700 text-slate-400 ml-1">{entries.length}</span>
        )}
      </button>
      {show && (
        <div className="mt-1 space-y-1.5">
          {entries.map(([k, v]: [string, string], i) => (
            <div key={i} className="flex items-center gap-2">
              <input
                value={k}
                onChange={(e) => updatePair(k, e.target.value, v)}
                placeholder="source field"
                className="flex-1 bg-slate-900 border border-slate-700 rounded px-2 py-1 text-xs text-slate-300 font-mono focus:outline-none focus:border-slate-500"
              />
              <span className="text-slate-600 text-xs">→</span>
              <input
                value={v}
                onChange={(e) => updatePair(k, k, e.target.value)}
                placeholder="dest field"
                className="flex-1 bg-slate-900 border border-slate-700 rounded px-2 py-1 text-xs text-slate-300 font-mono focus:outline-none focus:border-slate-500"
              />
              <button onClick={() => removePair(k)} className="text-slate-600 hover:text-red-400 transition">
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
          <button
            onClick={addPair}
            className="text-xs text-slate-500 hover:text-slate-300 transition flex items-center gap-1"
          >
            <Plus className="w-3 h-3" /> Add mapping
          </button>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// StepCard
// ---------------------------------------------------------------------------

interface StepCardProps {
  step: StepRow;
  index: number;
  agents: TradingAgent[];
  execResult?: NodeResult;
  isRunning?: boolean;
  onUpdate: (partial: Partial<StepRow>) => void;
  onDelete: () => void;
  isFirst: boolean;
}

const StepCard: React.FC<StepCardProps> = ({
  step, index, agents, execResult, isRunning, onUpdate, onDelete, isFirst,
}) => {
  const inputCls = 'bg-slate-900 border border-slate-700 rounded-lg px-2.5 py-1.5 text-white text-sm focus:outline-none focus:border-blue-500 transition';

  return (
    <div className={`bg-slate-800/60 border rounded-xl p-4 transition ${
      execResult?.success ? 'border-green-500/30' :
      execResult && !execResult.success ? 'border-red-500/30' :
      isRunning ? 'border-blue-500/40' : 'border-slate-700'
    }`}>
      <div className="flex items-start gap-3">
        {/* Step number */}
        <div className="flex-shrink-0 w-7 h-7 rounded-full bg-slate-700 flex items-center justify-center text-xs font-bold text-slate-400">
          {index + 1}
        </div>

        <div className="flex-1 space-y-3">
          {/* Agent + label row */}
          <div className="flex items-center gap-3 flex-wrap">
            <select
              value={step.agent_id}
              onChange={(e) => onUpdate({ agent_id: e.target.value })}
              className={`${inputCls} flex-1 min-w-[180px]`}
            >
              {agents.length === 0 && <option value="">No agents</option>}
              {agents.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name} ({a.agent_type})
                </option>
              ))}
            </select>
            <input
              value={step.label}
              onChange={(e) => onUpdate({ label: e.target.value })}
              placeholder={`Step ${index + 1} label`}
              className={`${inputCls} flex-1 min-w-[140px]`}
            />
            <NodeStatusBadge result={execResult} running={isRunning} />
            <button
              onClick={onDelete}
              className="p-1.5 rounded-md hover:bg-red-500/20 text-slate-500 hover:text-red-400 transition flex-shrink-0"
              title="Remove step"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>

          {/* Condition (for all except first) */}
          {!isFirst && (
            <DataMappingEditor
              mapping={step.dataMapping}
              show={step.showMapping}
              onToggle={() => onUpdate({ showMapping: !step.showMapping })}
              onChange={(m) => onUpdate({ dataMapping: m })}
            />
          )}

          {/* Execution result preview */}
          {execResult?.result && (
            <details className="text-xs">
              <summary className="text-slate-500 cursor-pointer hover:text-slate-300">View result</summary>
              <pre className="mt-1.5 bg-slate-900 rounded p-2 text-slate-300 overflow-x-auto max-h-32 text-xs leading-relaxed">
                {JSON.stringify(execResult.result, null, 2)}
              </pre>
            </details>
          )}
          {execResult?.error && (
            <p className="text-xs text-red-400">{execResult.error}</p>
          )}
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// ActionCard
// ---------------------------------------------------------------------------

interface ActionCardProps {
  action: ActionForm;
  steps: StepRow[];
  agents: TradingAgent[];
  connections: DataConnection[];
  onUpdate: (partial: Partial<ActionForm>) => void;
  onDelete: () => void;
}

const ACTION_META = {
  execute_trade: { label: 'Execute Trade', color: 'text-emerald-300 bg-emerald-400/10 border-emerald-400/30' },
  notify_slack: { label: 'Notify Slack', color: 'text-yellow-300 bg-yellow-400/10 border-yellow-400/30' },
  push_mcp: { label: 'Push to MCP', color: 'text-purple-300 bg-purple-400/10 border-purple-400/30' },
  notify_telegram: { label: 'Notify Telegram', color: 'text-sky-300 bg-sky-400/10 border-sky-400/30' },
  notify_mattermost: { label: 'Notify Mattermost', color: 'text-indigo-300 bg-indigo-400/10 border-indigo-400/30' },
  trigger_signal_window: { label: 'Trigger Signal Window', color: 'text-amber-300 bg-amber-400/10 border-amber-400/30' },
};

const ActionCard: React.FC<ActionCardProps> = ({ action, steps, agents, connections, onUpdate, onDelete }) => {
  const inputCls = 'w-full bg-slate-900 border border-slate-700 rounded-lg px-2.5 py-1.5 text-white text-sm focus:outline-none focus:border-blue-500';
  const meta = ACTION_META[action.action_type];
  const mcpConns = connections.filter((c) => c.connection_type === 'mcp_server');
  const exchangeConns = connections.filter((c) => c.connection_type === 'exchange');
  const telegramConns = connections.filter((c) => c.connection_type === 'telegram');
  const mattermostConns = connections.filter((c) => c.connection_type === 'mattermost');

  const triggerStep = steps.find((s) => s.nodeId === action.node_id);
  const triggerAgent = agents.find((a) => a.id === triggerStep?.agent_id);

  return (
    <div className={`bg-slate-800/60 border rounded-xl p-4 space-y-3`} style={{ borderColor: 'rgba(100,116,139,0.4)' }}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className={`px-2 py-0.5 rounded text-xs font-bold border ${meta.color}`}>{meta.label}</span>
          <span className="text-slate-500 text-xs">
            triggered by: <span className="text-slate-300">{triggerAgent?.name || 'unknown'}</span>
          </span>
        </div>
        <button
          onClick={onDelete}
          className="p-1.5 rounded-md hover:bg-red-500/20 text-slate-500 hover:text-red-400 transition"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Trigger step selector */}
      <div>
        <label className="block text-xs text-slate-400 mb-1">Triggered by step</label>
        <select value={action.node_id} onChange={(e) => onUpdate({ node_id: e.target.value })} className={inputCls}>
          {steps.map((s, i) => {
            const a = agents.find((ag) => ag.id === s.agent_id);
            return (
              <option key={s.nodeId} value={s.nodeId}>
                Step {i + 1}: {a?.name || 'Unknown'} — {s.label || `Step ${i + 1}`}
              </option>
            );
          })}
        </select>
      </div>

      {/* execute_trade fields */}
      {action.action_type === 'execute_trade' && (
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs text-slate-400 mb-1">Mode</label>
            <select value={action.mode} onChange={(e) => onUpdate({ mode: e.target.value as 'live' | 'paper' })} className={inputCls}>
              <option value="paper">Paper (simulate)</option>
              <option value="live">Live (real orders)</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-slate-400 mb-1">Symbol</label>
            <input
              value={action.symbol}
              onChange={(e) => onUpdate({ symbol: e.target.value })}
              placeholder="BTC/USDT"
              className={inputCls}
            />
          </div>
          {action.mode === 'live' && (
            <div className="col-span-2">
              <label className="block text-xs text-slate-400 mb-1">Exchange Connection</label>
              <select value={action.connection_id} onChange={(e) => onUpdate({ connection_id: e.target.value })} className={inputCls}>
                <option value="">Select exchange connection...</option>
                {exchangeConns.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
          )}
          <div className="col-span-2">
            <label className="block text-xs text-slate-400 mb-1">Position size field (from agent result)</label>
            <input
              value={action.qty_field}
              onChange={(e) => onUpdate({ qty_field: e.target.value })}
              placeholder="position_size_pct"
              className={`${inputCls} font-mono`}
            />
          </div>
        </div>
      )}

      {/* notify_slack fields */}
      {action.action_type === 'notify_slack' && (
        <div>
          <label className="block text-xs text-slate-400 mb-1">
            Message template <span className="text-slate-600 font-normal">(use {'{signal}'}, {'{confidence}'}, {'{reasoning}'})</span>
          </label>
          <textarea
            value={action.slack_template}
            onChange={(e) => onUpdate({ slack_template: e.target.value })}
            rows={3}
            placeholder="Signal: {signal} | Confidence: {confidence} | {reasoning}"
            className={`${inputCls} resize-none`}
          />
        </div>
      )}

      {/* push_mcp fields */}
      {action.action_type === 'push_mcp' && (
        <div>
          <label className="block text-xs text-slate-400 mb-1">MCP Server Connection</label>
          <select value={action.mcp_connection_id} onChange={(e) => onUpdate({ mcp_connection_id: e.target.value })} className={inputCls}>
            <option value="">Select MCP connection...</option>
            {mcpConns.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          {mcpConns.length === 0 && (
            <p className="text-xs text-slate-500 mt-1">No MCP server connections defined. Create one on the Connections page.</p>
          )}
        </div>
      )}

      {/* notify_telegram fields */}
      {action.action_type === 'notify_telegram' && (
        <div className="space-y-3">
          <div>
            <label className="block text-xs text-slate-400 mb-1">Telegram Connection</label>
            <select
              value={action.telegram_connection_id}
              onChange={(e) => onUpdate({ telegram_connection_id: e.target.value })}
              className={inputCls}
            >
              <option value="">Select Telegram connection...</option>
              {telegramConns.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            {telegramConns.length === 0 && (
              <p className="text-xs text-slate-500 mt-1">No Telegram connections defined. Create one on the Connections page.</p>
            )}
          </div>
          <div>
            <label className="block text-xs text-slate-400 mb-1">
              Chat override <span className="text-slate-600 font-normal">(leave blank to use connection default)</span>
            </label>
            <input
              value={action.telegram_chat}
              onChange={(e) => onUpdate({ telegram_chat: e.target.value })}
              placeholder="@mychannel or -100123456789"
              className={inputCls}
            />
          </div>
          <div>
            <label className="block text-xs text-slate-400 mb-1">
              Message template <span className="text-slate-600 font-normal">(use {'{signal}'}, {'{confidence}'}, {'{reasoning}'})</span>
            </label>
            <textarea
              value={action.telegram_template}
              onChange={(e) => onUpdate({ telegram_template: e.target.value })}
              rows={3}
              placeholder="🤖 {agent_name} | signal: {signal} | confidence: {confidence}"
              className={`${inputCls} resize-none`}
            />
          </div>
        </div>
      )}

      {/* notify_mattermost fields */}
      {action.action_type === 'notify_mattermost' && (
        <div className="space-y-3">
          <div className="bg-indigo-500/10 border border-indigo-500/20 rounded-lg p-3 text-xs text-indigo-300">
            Post trade signals to Mattermost at <strong>chat.privacypal.ai</strong>. Reply with <strong>accept</strong>, <strong>reject</strong>, or <strong>ask: your question</strong> for interactive feedback.
          </div>
          <div>
            <label className="block text-xs text-slate-400 mb-1">Mattermost Connection</label>
            <select
              value={action.mattermost_connection_id}
              onChange={(e) => onUpdate({ mattermost_connection_id: e.target.value })}
              className={inputCls}
            >
              <option value="">Select Mattermost connection...</option>
              {mattermostConns.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            {mattermostConns.length === 0 && (
              <p className="text-xs text-slate-500 mt-1">No Mattermost connections. Create one on the Connections page.</p>
            )}
          </div>
          <div>
            <label className="block text-xs text-slate-400 mb-1">
              Channel ID <span className="text-slate-600 font-normal">(required for bot token; optional for webhook)</span>
            </label>
            <input
              value={action.mattermost_channel_id}
              onChange={(e) => onUpdate({ mattermost_channel_id: e.target.value })}
              placeholder="e.g. xxxxxxxxxxxxxxxxxxxxxxxxxx"
              className={inputCls}
            />
          </div>
          <div>
            <label className="block text-xs text-slate-400 mb-1">
              Message template <span className="text-slate-600 font-normal">(use {'{signal}'}, {'{confidence}'}, {'{reasoning}'})</span>
            </label>
            <textarea
              value={action.mattermost_template}
              onChange={(e) => onUpdate({ mattermost_template: e.target.value })}
              rows={3}
              placeholder="Leave blank for default trade signal format"
              className={`${inputCls} resize-none`}
            />
          </div>
        </div>
      )}

      {/* trigger_signal_window fields */}
      {action.action_type === 'trigger_signal_window' && (
        <div className="space-y-3">
          <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3 text-xs text-amber-300">
            When the trigger step completes, this action starts a <strong>continuous trading window</strong> for the target signal agent.
            The agent runs autonomously for the specified duration, self-pacing via <code>next_check_in_seconds</code> in its LLM output.
            Use this to let an Analyst wake up a Signal Agent based on a macro condition (e.g. bullish tweet sentiment).
          </div>
          <div>
            <label className="block text-xs text-slate-400 mb-1">Target Signal Agent</label>
            <select
              value={action.signal_agent_id}
              onChange={(e) => onUpdate({ signal_agent_id: e.target.value })}
              className={inputCls}
            >
              <option value="">Select signal agent...</option>
              {agents.filter((a) => a.agent_type === 'signal').map((a) => (
                <option key={a.id} value={a.id}>{a.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs text-slate-400 mb-1">Window Duration</label>
            <select
              value={action.trigger_window_duration}
              onChange={(e) => onUpdate({ trigger_window_duration: parseInt(e.target.value) })}
              className={inputCls}
            >
              <option value={30}>30 minutes</option>
              <option value={60}>1 hour</option>
              <option value={120}>2 hours</option>
              <option value={180}>3 hours</option>
              <option value={240}>4 hours</option>
            </select>
          </div>
          <p className="text-xs text-slate-500">
            Signal agent runs <strong className="text-slate-400">continuously</strong> for{' '}
            <strong className="text-slate-400">{action.trigger_window_duration} min</strong> ({action.trigger_window_duration / 60} hr).
            The LLM controls its own analysis cadence.
          </p>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// ExecutionHistoryPanel
// ---------------------------------------------------------------------------

interface HistoryPanelProps {
  pipelineId: string;
  nodeToAgent: Map<string, string>;
}

const ExecutionHistoryPanel: React.FC<HistoryPanelProps> = ({ pipelineId, nodeToAgent }) => {
  const [executions, setExecutions] = useState<PipelineExecution[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    pipelineSvc.getPipelineExecutions(pipelineId, 20)
      .then(setExecutions)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [pipelineId]);

  if (loading) return <div className="text-slate-400 text-sm py-8 text-center">Loading history...</div>;
  if (executions.length === 0) return (
    <div className="text-slate-500 text-sm py-8 text-center">No executions yet. Run the pipeline to see history here.</div>
  );

  return (
    <div className="space-y-2">
      {executions.map((exec) => {
        const isExpanded = expandedId === exec.id;
        const nodeResults = (exec.node_results ?? {}) as Record<string, NodeResult>;
        const nodeCount = Object.keys(nodeResults).length;
        const successCount = Object.values(nodeResults).filter((r) => r.success).length;

        return (
          <div key={exec.id} className="bg-slate-800/60 border border-slate-700 rounded-xl overflow-hidden">
            <button
              className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-700/30 transition text-left"
              onClick={() => setExpandedId(isExpanded ? null : exec.id)}
            >
              <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${
                exec.status === 'completed' ? 'bg-green-400' :
                exec.status === 'failed' ? 'bg-red-400' : 'bg-blue-400 animate-pulse'
              }`} />
              <span className={`text-xs font-semibold px-2 py-0.5 rounded ${
                exec.status === 'completed' ? 'bg-green-400/10 text-green-300' :
                exec.status === 'failed' ? 'bg-red-400/10 text-red-300' : 'bg-blue-400/10 text-blue-300'
              }`}>{exec.status}</span>
              <span className="text-slate-400 text-xs">{formatDate(exec.started_at)}</span>
              <span className="text-slate-600 text-xs">·</span>
              <span className="text-slate-500 text-xs flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {durationLabel(exec.started_at, exec.completed_at)}
              </span>
              <span className="text-slate-500 text-xs ml-auto">
                {successCount}/{nodeCount} steps succeeded
              </span>
              {isExpanded ? <ChevronDown className="w-4 h-4 text-slate-500 flex-shrink-0" /> : <ChevronRight className="w-4 h-4 text-slate-500 flex-shrink-0" />}
            </button>

            {isExpanded && (
              <div className="border-t border-slate-700 px-4 pb-4 pt-3 space-y-2">
                {exec.error && (
                  <p className="text-red-400 text-xs bg-red-400/10 rounded p-2">{exec.error}</p>
                )}
                {Object.entries((exec.node_results ?? {}) as Record<string, NodeResult>).map(([nodeId, r]) => (
                  <details key={nodeId} className="bg-slate-900/50 rounded-lg">
                    <summary className="flex items-center gap-2 px-3 py-2 cursor-pointer text-xs">
                      <NodeStatusBadge result={r} />
                      <span className="text-slate-400">{nodeToAgent.get(nodeId) || nodeId}</span>
                      {r.duration_ms && <span className="text-slate-600 ml-auto">{r.duration_ms.toFixed(0)}ms</span>}
                    </summary>
                    <div className="px-3 pb-3">
                      {r.error && <p className="text-red-400 text-xs mb-2">{r.error}</p>}
                      {r.result && (
                        <pre className="text-xs text-slate-300 bg-slate-900 rounded p-2 overflow-x-auto max-h-48 leading-relaxed">
                          {JSON.stringify(r.result, null, 2)}
                        </pre>
                      )}
                    </div>
                  </details>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

// ---------------------------------------------------------------------------
// PipelineListCard
// ---------------------------------------------------------------------------

interface PipelineListCardProps {
  pipeline: AgentPipeline;
  selected: boolean;
  onClick: () => void;
  onDelete: () => void;
}

const PipelineListCard: React.FC<PipelineListCardProps> = ({ pipeline, selected, onClick, onDelete }) => (
  <div
    className={`rounded-xl border p-3.5 cursor-pointer transition ${
      selected
        ? 'border-blue-500/60 bg-blue-500/5'
        : 'border-slate-700 bg-slate-800/50 hover:border-slate-600'
    }`}
    onClick={onClick}
  >
    <div className="flex items-start justify-between gap-2">
      <div className="min-w-0">
        <h3 className="font-semibold text-white text-sm truncate">{pipeline.name}</h3>
        {pipeline.description && (
          <p className="text-slate-500 text-xs mt-0.5 truncate">{pipeline.description}</p>
        )}
      </div>
      <button
        onClick={(e) => { e.stopPropagation(); onDelete(); }}
        className="p-1 rounded hover:bg-red-500/20 text-slate-600 hover:text-red-400 transition flex-shrink-0"
      >
        <Trash2 className="w-3 h-3" />
      </button>
    </div>
    <div className="flex items-center gap-3 mt-2 text-xs text-slate-500">
      <span className="flex items-center gap-1">
        <GitBranch className="w-3 h-3" />
        {pipeline.nodes.length} step{pipeline.nodes.length !== 1 ? 's' : ''}
      </span>
      {pipeline.action_nodes.length > 0 && (
        <span className="flex items-center gap-1">
          <Zap className="w-3 h-3 text-yellow-400/70" />
          {pipeline.action_nodes.length} action{pipeline.action_nodes.length !== 1 ? 's' : ''}
        </span>
      )}
      {!pipeline.is_active && (
        <span className="text-red-400/70">inactive</span>
      )}
      {pipeline.schedule_enabled && (
        <span className="flex items-center gap-1 text-amber-400/70">
          <Clock className="w-3 h-3" />
          scheduled
        </span>
      )}
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Main Page
// ---------------------------------------------------------------------------

const PipelinesPage: React.FC = () => {
  const [pipelines, setPipelines] = useState<AgentPipeline[]>([]);
  const [agents, setAgents] = useState<TradingAgent[]>([]);
  const [connections, setConnections] = useState<DataConnection[]>([]);
  const [loading, setLoading] = useState(true);

  const [selectedPipeline, setSelectedPipeline] = useState<AgentPipeline | null>(null);
  const [tab, setTab] = useState<'builder' | 'history'>('builder');

  // Builder form state
  const [pName, setPName] = useState('');
  const [pDesc, setPDesc] = useState('');
  const [steps, setSteps] = useState<StepRow[]>([]);
  const [actionForms, setActionForms] = useState<ActionForm[]>([]);
  const [isDirty, setIsDirty] = useState(false);

  // Pipeline schedule state
  const DEFAULT_PIPELINE_SCHEDULE_DAYS = ['mon', 'tue', 'wed', 'thu', 'fri'];
  const [pScheduleEnabled, setPScheduleEnabled] = useState(false);
  const [pScheduleDays, setPScheduleDays] = useState<string[]>(DEFAULT_PIPELINE_SCHEDULE_DAYS);
  const [pScheduleRunAt, setPScheduleRunAt] = useState('09:00');
  const [pScheduleTz, setPScheduleTz] = useState('America/New_York');

  // Execution state
  const [running, setRunning] = useState(false);
  const [execResult, setExecResult] = useState<PipelineExecution | null>(null);

  // UI state
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<AgentPipeline | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Load everything
  useEffect(() => {
    Promise.all([pipelineSvc.getPipelines(), getAgents(), getConnections()])
      .then(([p, a, c]) => { setPipelines(p); setAgents(a); setConnections(c); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const makeBlankStep = useCallback((): StepRow => ({
    nodeId: genId(),
    agent_id: agents[0]?.id || '',
    label: '',
    inCondition: null,
    dataMapping: {},
    showMapping: false,
  }), [agents]);

  const loadPipeline = useCallback((p: AgentPipeline) => {
    setSelectedPipeline(p);
    setPName(p.name);
    setPDesc(p.description || '');
    const { steps: s, actions: a } = apiToSteps(p);
    setSteps(s);
    setActionForms(a);
    setPScheduleEnabled(p.schedule_enabled ?? false);
    setPScheduleDays(p.schedule_days ?? DEFAULT_PIPELINE_SCHEDULE_DAYS);
    setPScheduleRunAt(p.schedule_run_at ?? '09:00');
    setPScheduleTz(p.schedule_timezone ?? 'America/New_York');
    setExecResult(null);
    setIsDirty(false);
    setTab('builder');
    setError(null);
  }, []);

  const startNew = useCallback(() => {
    setSelectedPipeline(null);
    setPName('New Pipeline');
    setPDesc('');
    setSteps([{
      nodeId: genId(),
      agent_id: agents[0]?.id || '',
      label: 'Step 1',
      inCondition: null,
      dataMapping: {},
      showMapping: false,
    }]);
    setActionForms([]);
    setPScheduleEnabled(false);
    setPScheduleDays(DEFAULT_PIPELINE_SCHEDULE_DAYS);
    setPScheduleRunAt('09:00');
    setPScheduleTz('America/New_York');
    setExecResult(null);
    setIsDirty(false);
    setTab('builder');
    setError(null);
  }, [agents]);

  const updateStep = (index: number, partial: Partial<StepRow>) => {
    setSteps((prev) => prev.map((s, i) => i === index ? { ...s, ...partial } : s));
    setIsDirty(true);
  };

  const addStep = () => {
    setSteps((prev) => [...prev, makeBlankStep()]);
    setIsDirty(true);
  };

  const removeStep = (index: number) => {
    setSteps((prev) => prev.filter((_, i) => i !== index));
    setIsDirty(true);
  };

  const addAction = (type: ActionForm['action_type']) => {
    const newAction: ActionForm = {
      id: genId(),
      node_id: steps[steps.length - 1]?.nodeId || '',
      action_type: type,
      mode: 'paper',
      connection_id: '',
      symbol: '',
      qty_field: 'position_size_pct',
      slack_template: '',
      mcp_connection_id: '',
      telegram_connection_id: '',
      telegram_chat: '',
      telegram_template: '',
      mattermost_connection_id: '',
      mattermost_channel_id: '',
      mattermost_template: '',
      signal_agent_id: '',
      trigger_window_duration: 60,
    };
    setActionForms((prev) => [...prev, newAction]);
    setIsDirty(true);
  };

  const updateAction = (id: string, partial: Partial<ActionForm>) => {
    setActionForms((prev) => prev.map((a) => a.id === id ? { ...a, ...partial } : a));
    setIsDirty(true);
  };

  const removeAction = (id: string) => {
    setActionForms((prev) => prev.filter((a) => a.id !== id));
    setIsDirty(true);
  };

  const handleSave = async () => {
    if (!pName.trim()) return;
    setSaving(true);
    setError(null);
    try {
      const { nodes, edges, entry_node, action_nodes } = stepsToApi(steps, actionForms);
      const schedulePayload = {
        schedule_enabled: pScheduleEnabled,
        schedule_days: pScheduleDays,
        schedule_run_at: pScheduleRunAt || null,
        schedule_timezone: pScheduleTz,
      };
      const payload: AgentPipelineCreate = { name: pName, description: pDesc, nodes, edges, entry_node, action_nodes, ...schedulePayload };
      if (selectedPipeline) {
        const updated = await pipelineSvc.updatePipeline(selectedPipeline.id, payload as AgentPipelineUpdate);
        setSelectedPipeline(updated);
        setPipelines((prev) => prev.map((p) => p.id === updated.id ? updated : p));
      } else {
        const created = await pipelineSvc.createPipeline(payload);
        setSelectedPipeline(created);
        setPipelines((prev) => [...prev, created]);
      }
      setIsDirty(false);
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleRun = async () => {
    if (!selectedPipeline) return;
    setRunning(true);
    setExecResult(null);
    setError(null);
    try {
      const exec = await pipelineSvc.executePipeline(selectedPipeline.id);
      setExecResult(exec);
    } catch (err: any) {
      const detail = err?.response?.data?.detail ?? err?.response?.data?.error;
      const msg = err?.message ?? 'Execution failed';
      const isNetwork = msg === 'Network Error' || err?.code === 'ECONNABORTED' || err?.code === 'ERR_NETWORK';
      setError(
        detail || (isNetwork
          ? `${msg} — Pipeline can take 30–90s. Check backend logs: docker compose logs backend`
          : msg)
      );
    } finally {
      setRunning(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await pipelineSvc.deletePipeline(deleteTarget.id);
      setPipelines((prev) => prev.filter((p) => p.id !== deleteTarget.id));
      if (selectedPipeline?.id === deleteTarget.id) setSelectedPipeline(null);
      setDeleteTarget(null);
    } catch (err) {
      console.error('Delete failed', err);
    }
  };

  // Map node_id → agent name for history panel
  const nodeToAgent = new Map<string, string>();
  if (selectedPipeline) {
    for (const node of selectedPipeline.nodes) {
      const agent = agents.find((a) => a.id === node.agent_id);
      nodeToAgent.set(node.id, agent ? `${agent.name} (${node.label || 'step'})` : node.label || node.id);
    }
  }

  const hasBuilder = selectedPipeline !== null || steps.length > 0;

  return (
    <div className="h-full flex flex-col">
      {/* Page header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-4xl font-bold text-white mb-2">Agent Pipelines</h1>
          <p className="text-slate-400">
            Chain agents together — output of one becomes input of the next, with conditional edges and trade actions
          </p>
        </div>
      </div>

      <div className="flex gap-6 flex-1 min-h-0">
        {/* ── Left panel: pipeline list ── */}
        <div className="w-64 flex-shrink-0 flex flex-col gap-3">
          <button
            onClick={startNew}
            className="flex items-center justify-center gap-2 w-full py-2.5 rounded-xl border border-dashed border-slate-600 hover:border-blue-500 hover:bg-blue-500/5 text-slate-400 hover:text-blue-300 text-sm transition"
          >
            <Plus className="w-4 h-4" />
            New Pipeline
          </button>

          {loading ? (
            <div className="text-slate-500 text-sm text-center py-4">Loading...</div>
          ) : pipelines.length === 0 ? (
            <div className="text-slate-600 text-xs text-center py-8">
              No pipelines yet. Click &quot;New Pipeline&quot; to start building.
            </div>
          ) : (
            pipelines.map((p) => (
              <PipelineListCard
                key={p.id}
                pipeline={p}
                selected={selectedPipeline?.id === p.id}
                onClick={() => loadPipeline(p)}
                onDelete={() => setDeleteTarget(p)}
              />
            ))
          )}
        </div>

        {/* ── Right panel: builder / history ── */}
        {hasBuilder ? (
          <div className="flex-1 flex flex-col min-w-0 gap-4 overflow-y-auto pr-1">
            {/* Tab bar + name */}
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-1 bg-slate-800/50 border border-slate-700 rounded-lg p-1">
                <button
                  onClick={() => setTab('builder')}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition ${
                    tab === 'builder' ? 'bg-blue-500 text-white' : 'text-slate-400 hover:text-white'
                  }`}
                >
                  <GitBranch className="w-3.5 h-3.5 inline mr-1.5" />
                  Builder
                </button>
                {selectedPipeline && (
                  <button
                    onClick={() => setTab('history')}
                    className={`px-3 py-1.5 rounded-md text-sm font-medium transition ${
                      tab === 'history' ? 'bg-blue-500 text-white' : 'text-slate-400 hover:text-white'
                    }`}
                  >
                    <History className="w-3.5 h-3.5 inline mr-1.5" />
                    History
                  </button>
                )}
              </div>

              {/* Toolbar */}
              {tab === 'builder' && (
                <div className="flex items-center gap-2">
                  {error && <span className="text-red-400 text-xs mr-1">{error}</span>}
                  <button
                    onClick={handleSave}
                    disabled={saving || !pName.trim()}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-700 hover:bg-slate-600 text-white text-sm disabled:opacity-50 transition"
                  >
                    {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                    {isDirty ? 'Save*' : 'Save'}
                  </button>
                  {selectedPipeline && (
                    <button
                      onClick={handleRun}
                      disabled={running || isDirty}
                      className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg bg-green-500 hover:bg-green-600 text-white text-sm font-medium disabled:opacity-50 transition"
                      title={isDirty ? 'Save pipeline before running' : 'Run pipeline'}
                    >
                      {running ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />}
                      Run
                    </button>
                  )}
                </div>
              )}
            </div>

            {tab === 'builder' && (
              <>
                {/* Pipeline metadata */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4 space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-slate-400 mb-1">Pipeline Name <span className="text-red-400">*</span></label>
                      <input
                        value={pName}
                        onChange={(e) => { setPName(e.target.value); setIsDirty(true); }}
                        placeholder="e.g. Signal → Analyst → Trade"
                        className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-1.5 text-white text-sm focus:outline-none focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-slate-400 mb-1">Description</label>
                      <input
                        value={pDesc}
                        onChange={(e) => { setPDesc(e.target.value); setIsDirty(true); }}
                        placeholder="What does this pipeline do?"
                        className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-1.5 text-white text-sm focus:outline-none focus:border-blue-500"
                      />
                    </div>
                  </div>
                </div>

                {/* Pipeline Schedule */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-sm font-semibold text-slate-300">
                      <Clock className="w-4 h-4 text-amber-400" />
                      Pipeline Schedule
                      {pScheduleEnabled && (
                        <span className="text-xs font-normal px-2 py-0.5 rounded-full bg-amber-400/10 border border-amber-400/20 text-amber-300">active</span>
                      )}
                    </div>
                    <button
                      onClick={() => { setPScheduleEnabled(e => !e); setIsDirty(true); }}
                      className={`relative w-10 h-5 rounded-full transition ${pScheduleEnabled ? 'bg-amber-500' : 'bg-slate-600'}`}
                    >
                      <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${pScheduleEnabled ? 'translate-x-5' : ''}`} />
                    </button>
                  </div>

                  {pScheduleEnabled && (
                    <div className="space-y-3 pt-1">
                      <div className="flex flex-wrap gap-1.5">
                        {(['mon','tue','wed','thu','fri','sat','sun'] as const).map(day => (
                          <button
                            key={day}
                            onClick={() => {
                              setPScheduleDays(prev => prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day]);
                              setIsDirty(true);
                            }}
                            className={`px-2.5 py-1 rounded-md text-xs font-semibold uppercase transition border ${
                              pScheduleDays.includes(day)
                                ? 'bg-amber-500/20 border-amber-500/50 text-amber-300'
                                : 'bg-slate-800 border-slate-600 text-slate-400 hover:border-slate-500'
                            }`}
                          >
                            {day}
                          </button>
                        ))}
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs text-slate-400 mb-1">Run at</label>
                          <input
                            type="time"
                            value={pScheduleRunAt}
                            onChange={(e) => { setPScheduleRunAt(e.target.value); setIsDirty(true); }}
                            className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-1.5 text-white text-sm focus:outline-none focus:border-amber-500"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-slate-400 mb-1">Timezone</label>
                          <select
                            value={pScheduleTz}
                            onChange={(e) => { setPScheduleTz(e.target.value); setIsDirty(true); }}
                            className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-1.5 text-white text-sm focus:outline-none focus:border-amber-500"
                          >
                            <option value="America/New_York">Eastern (ET)</option>
                            <option value="America/Chicago">Central (CT)</option>
                            <option value="America/Denver">Mountain (MT)</option>
                            <option value="America/Los_Angeles">Pacific (PT)</option>
                            <option value="UTC">UTC</option>
                          </select>
                        </div>
                      </div>
                      <p className="text-xs text-amber-400/70">
                        Pipeline will run automatically at <strong>{pScheduleRunAt}</strong> ({pScheduleTz}) on{' '}
                        <strong>{pScheduleDays.map(d => d.charAt(0).toUpperCase() + d.slice(1)).join(', ')}</strong>.
                        Save to activate.
                      </p>
                    </div>
                  )}
                </div>

                {/* Execution result banner */}
                {execResult && (
                  <div className={`flex items-center gap-3 rounded-xl border px-4 py-3 text-sm ${
                    execResult.status === 'completed'
                      ? 'bg-green-900/30 border-green-500/40 text-green-200'
                      : 'bg-red-900/30 border-red-500/40 text-red-200'
                  }`}>
                    {execResult.status === 'completed'
                      ? <CheckCircle className="w-4 h-4 flex-shrink-0" />
                      : <AlertCircle className="w-4 h-4 flex-shrink-0" />}
                    <span className="font-medium">Pipeline {execResult.status}</span>
                    <span className="opacity-70">·</span>
                    <span className="opacity-70">{durationLabel(execResult.started_at, execResult.completed_at)}</span>
                    <span className="opacity-70">·</span>
                    <span className="opacity-70">
                      {Object.values(execResult.node_results as Record<string, NodeResult>).filter((r) => r.success).length}/
                      {Object.values(execResult.node_results).length} steps succeeded
                    </span>
                    <button onClick={() => setExecResult(null)} className="ml-auto opacity-60 hover:opacity-100">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}

                {/* Steps */}
                <div className="space-y-1">
                  {steps.map((step, i) => (
                    <React.Fragment key={step.nodeId}>
                      {i > 0 && (
                        <ConditionEditor
                          condition={step.inCondition}
                          onChange={(c) => updateStep(i, { inCondition: c })}
                        />
                      )}
                      <StepCard
                        step={step}
                        index={i}
                        agents={agents}
                        isFirst={i === 0}
                        execResult={execResult?.node_results?.[step.nodeId]}
                        isRunning={running}
                        onUpdate={(p) => updateStep(i, p)}
                        onDelete={() => removeStep(i)}
                      />
                    </React.Fragment>
                  ))}
                </div>

                <button
                  onClick={addStep}
                  className="flex items-center justify-center gap-2 py-2.5 rounded-xl border border-dashed border-slate-700 hover:border-slate-500 text-slate-500 hover:text-slate-300 text-sm transition"
                >
                  <Plus className="w-4 h-4" />
                  Add Step
                </button>

                {/* Action Nodes */}
                {(actionForms.length > 0 || steps.length > 0) && (
                  <div className="bg-slate-800/30 border border-slate-700/50 rounded-xl p-4 space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-sm font-semibold text-slate-300">
                        <Zap className="w-4 h-4 text-yellow-400" />
                        Action Nodes
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-slate-500">Add:</span>
                        {(['execute_trade', 'notify_slack', 'push_mcp', 'notify_telegram', 'notify_mattermost', 'trigger_signal_window'] as const).map((type) => (
                          <button
                            key={type}
                            onClick={() => addAction(type)}
                            className={`text-xs px-2.5 py-1 rounded-lg border transition ${ACTION_META[type].color} hover:opacity-80`}
                          >
                            + {ACTION_META[type].label}
                          </button>
                        ))}
                      </div>
                    </div>

                    {actionForms.length === 0 ? (
                      <p className="text-slate-600 text-xs">
                        No action nodes yet. Actions execute when their trigger step completes successfully.
                      </p>
                    ) : (
                      <div className="space-y-3">
                        {actionForms.map((a) => (
                          <ActionCard
                            key={a.id}
                            action={a}
                            steps={steps}
                            agents={agents}
                            connections={connections}
                            onUpdate={(p) => updateAction(a.id, p)}
                            onDelete={() => removeAction(a.id)}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}

            {tab === 'history' && selectedPipeline && (
              <ExecutionHistoryPanel pipelineId={selectedPipeline.id} nodeToAgent={nodeToAgent} />
            )}
          </div>
        ) : (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <Activity className="w-14 h-14 text-slate-700 mx-auto mb-4" />
              <p className="text-slate-400 mb-2">No pipeline selected</p>
              <p className="text-slate-600 text-sm">
                Select a pipeline from the list or create a new one to start building.
              </p>
            </div>
          </div>
        )}
      </div>

      <ConfirmationModal
        isOpen={!!deleteTarget}
        title="Delete Pipeline?"
        message={`Delete "${deleteTarget?.name}"? This will also delete all execution history.`}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
};

export default PipelinesPage;
